import BasePointModel from "./BasePointModel";
import map from "lodash/map";
import UserTypeStorage from "../utilities/UserTypeStorage";
import Utility from './../utilities/Utility';
import { Constant, variableOnlySearch } from '../const';
import { LinkModel } from "storm-react-diagrams";
import { updateLastChange } from "../utilities";

export default class BaseLinkModel extends LinkModel {
    /**
     * Link model
     * @module BaseLinkModel
     * @param {Object} source Source port model
     * @param {Object} target Target port model
     * @param {Object} props Pre-defined properties to be applied to model
     * @param {string} id If non-null, link model will use this as its ID
     * @param {Array} points If non-null and has at least 2 elements, use this as link anchor & terminal points
     */
    constructor(source, target, props = null, id = null, points = null) {
        super();
        if (id) {
            this.id = id;
        }
        this.points = [new BasePointModel(this, { x: 0, y: 0 }), new BasePointModel(this, { x: 0, y: 0 })];
        if (points && points.length >= 2) {
            this.points = [];
            for (var i = 0; i < points.length; i++) {
                this.points.push(new BasePointModel(this, { x: points[i].x, y: points[i].y }));
            }
        }

        this.props = {
            nodeTypeName: "Transition",
            colorClass: "primary",
            error: {},
            config: {
                condition: [],
                description: "",
                inputs: [],
                disabledInFlow: false
            },
            configView: [],
            permission: {}  
        };
        
        if (props) { // DEVDES2-917
            if(!props.config.hasOwnProperty('disabledInFlow')){
                props.config['disabledInFlow'] = false;
            }
            Object.assign(this.props, props);
        }

        this.DEFAULT_COLOR_CLASS = 'primary';
        
        if (this.props.config.condition) {
            // reset legacy serializedCondition
            this.props.config.serializedCondition = this.serializeCondition(this.props.config.condition);
        }
        this.previousPorts = {};
        // Suppress event broadcast and error update during instantiation, since it's way earlier than initial render
        this.suppressBroadcast = true;
        this.useCurrentError = true;
        this.setSourcePort(source);
        this.setTargetPort(target);
        // Turn off suppression
        this.useCurrentError = false;
        this.suppressBroadcast = false;
        this.selectEvent = new CustomEvent('ev-link-select', { detail: this.id });
    }

    /**
     * Set that the model's view is selected by user (or not)
     * @param {boolean} selected
     */
    setSelected(selected) {
        const currentSel = this.selected;
        super.setSelected(selected);
        if (currentSel !== selected) {
            document.dispatchEvent(this.selectEvent);
        }
    }

    getDefaultColorClass(){
        return this.DEFAULT_COLOR_CLASS;
    }

    /**
     * Get the node this link is pointing to
     * @returns {null|Object}
     */
    getTargetNode() {
        if (this.targetPort && this.targetPort.parentNode) {
            return this.targetPort.parentNode;
        }
        return null;
    }

    getSourceNode() {
        if (this.sourcePort && this.sourcePort.parentNode) {
            return this.sourcePort.parentNode;
        }
        return null;
    }

    getOutputSuggestions() {
        const sourceNode = this.getSourceNode();
        if (sourceNode) {
            return sourceNode.getOutputSuggestions();
        }
        return null;
    }

    /**
     * Set model property
     * @param {string} key
     * @param {*} valuej
     */
    setProperty(key, value) {
        this.props[key] = JSON.parse(JSON.stringify(value));
    }

    /**
     * Get model property
     * @param {string} key
     * @returns {*}
     */
    getProperty(key) {
        return this.props[key];
    }

    /**
     * Set model config, which corresponds to properties shown by the property panel
     * @param {string} key
     * @param {*} value
     */
    setConfig(key, value) {
        // do not include disabledInFlow events in collaboration
        if (key !== 'disabledInFlow' && this.props.config[key] !== JSON.parse(JSON.stringify(value))) {
            updateLastChange(this.props);
        }
        this.props.config[key] = JSON.parse(JSON.stringify(value));

        if(key === "disabledInFlow"){
            window.apm.postEventToCurrentView({ target: "request-disable-flow-from-link", link: this, value: value});
        }

        if (key === "condition") {
            // generate serializedCondition when condition changes
            this.props.config.serializedCondition = this.serializeCondition(value);

            // update transition inputs based on conditions
            this.props.config["inputs"] = [];
            var refs = {};
            for (var i = 0; i < value.length; i++) {
                for (var j = 0; j < value[i].length; j++) {
                    const cond = value[i][j];
                    const m = cond.match(variableOnlySearch);
                    if (!m) {
                        continue;
                    }
                    for (var mi = 0; mi < m.length; mi++) {
                        const sv = m[mi].split(".");
                        var v = null;
                        if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                            v = window.apm.getVariable(sv[1]);
                        }
                        if (sv[0] === Constant.k.SCOPE_MODULE) {
                            v = window.apm.getVariable(sv[1], window.apm.getCurrentDiagramWidget().props.node.id);
                        }
                        if (sv[0] === Constant.k.PARAMETERS) {
                            v = window.apm.getParameter(sv[1]);
                        }
                        if (v) {
                            refs[m[mi]] = true;
                        }
                    }
                }
            }
            const dedupRefs = Object.keys(refs);
            for (var i = 0; i < dedupRefs.length; i++) {
                this.props.config["inputs"].push({
                    id: Utility.uuid4(),
                    argumentName: dedupRefs[i],
                    type: "variable",
                    value: null,
                    varName: dedupRefs[i]
                });
            }
            // refresh reference list
            document.dispatchEvent(new CustomEvent("ev-property-item-list-refresh"));
        }

        this.checkSourceOutDegree();
    }

    /**
     * Get model config
     * @param {string} key
     * @returns {*}
     */
    getConfig(key) {
        return this.props.config[key];
    }

    /**
     * Update two-way reference of variables
     * @param {Array} oldInputs
     */
    updateVariables(oldInputs) {
        // Clean up old references
        if (oldInputs) {
            this.removeVariableRefs(oldInputs);
        }
        // Set new references
        const modId = window.apm.getCurrentDiagramWidget().props.node.id;
        if (this.getConfig("inputs")) {
            this.setVariables(this.getConfig("inputs"), modId, "set");
        }
    }

    /**
     * Remove variable references
     */
    removeVariableRefs(oldInputs = null) {
        var inputs = oldInputs;
        if (this.getConfig("inputs") && oldInputs === null) {
            inputs = this.getConfig("inputs");
        }
        if (inputs) {
            const modId = window.apm.getCurrentDiagramWidget().props.node.id;
            this.setVariables(inputs, modId, "del");
        }
    }

    setVariables(vars, modId, op) {
        for (var i = 0; i < vars.length; i++) {
            const v = vars[i];
            const scope = v.varName.split(".")[0];
            const varName = v.varName.split(".")[1];
            if (scope === Constant.k.SCOPE_APPLICATION) {
                if (op === "set") {
                    window.apm.setVariable(varName, v.type, modId);
                }
                if (op === "del") {
                    window.apm.removeVariable(varName, modId);
                }
            }
            if (scope === Constant.k.SCOPE_MODULE) {
                if (op === "set") {
                    window.apm.setVariable(varName, v.type, modId, modId);
                }
                if (op === "del") {
                    window.apm.removeVariable(varName, modId, modId);
                }
            }
        }
    }

    dfs(visited, op) {
        // Skip visited
        if (visited.links.hasOwnProperty(this.getID())) {
            return;
        }
        visited.links[this.getID()] = true;
        if (this.getTargetPort()) {
            const targetNode = this.getTargetPort().getParent();
            if (op.type === "configEvaluation") {
                // Only check unvisited nodes
                if (!visited.nodes.hasOwnProperty(targetNode.id)) {
                    // Since edge can't be reached, and the node has only this inbound edge (in-degree 1), node is unreachable
                    if (this.isIgnored && targetNode.getInLinks().length === 1) {
                        targetNode.setIgnored(true);
                    } else {
                        // Node is reachable either via another inbound edge, or via this edge
                        targetNode.setIgnored(false);
                    }
                }
            }
            // DFS
            targetNode.dfs(visited, op);
        }
    }

    setIgnored(isIgnored = false) {
        this.isIgnored = isIgnored;
        document.dispatchEvent(new CustomEvent('ev-graph-link-ignore', { detail: this.getID() }));
    }

    /**
     * Set permission
     * @param {string} group Group name
     * @param {Object} value Permission definition
     */
    setPermission(group, value) {
        this.props.permission[group] = value;
    }

    /**
     * Get permission
     * @param {string} group Group name
     * @returns {Object}
     */
    getPermission(group) {
        return this.props.permission[group];
    }

    /**
     * Get all permissions
     * @returns {Object}
     */
    getPermissions() {
        return this.props.permission;
    }

    /**
     * Get the port the link is previously connected to (before re-wiring)
     * @param {string} key Port type key
     * @returns {Object} Port model
     */
    getPreviousPort(key) {
        return this.previousPorts[key];
    }

    /**
     * Set the port the link will be previously connected to after re-wiring
     * @param {string} key Port type key
     * @param {Object} port Port model
     */
    setPreviousPort(key, port) {
        this.previousPorts[key] = port;
    }

    /**
     * Check the out-degree of the node this link is coming out of
     * Each link coming out of the said node without a condition adds to the out-degree
     * @param {boolean} ignoreSelf If true, out-degree count will exclude current model itself
     */
    checkSourceOutDegree(ignoreSelf = false) {
        const errs = Object.keys(this.props.error) || null;
        const userType = UserTypeStorage.getUserType();
        // DEVDES2-866
        if(errs && errs.length > 0) {
            errs.forEach(err => {
                if(err !== "free::" + this.id){
                    this.removeError(err);
                }
            });
        }
        if (this.useCurrentError && this.props && this.hasError()) {
            return;
        }
        const sourcePort = this.getSourcePort() ? this.getSourcePort() : this.getPreviousPort("source");
        if (sourcePort) {
            var outFreeLinks = 0;
            var outNameLinks = 0;
            const nodePorts = Object.values(sourcePort.getParent().getPorts());
            for (var i = 0; i < nodePorts.length; i++) {
                const p = nodePorts[i];
                const portLinks = Object.values(p.getLinks());
                for (var j = 0; j < portLinks.length; j++) {
                    const l = portLinks[j];
                    if (l.getSourcePort() && l.getSourcePort().getID() === p.getID() && !l.hasCondition()) {
                        outFreeLinks++;
                    }
                    if(l.getSourcePort() && l.getSourcePort().getID() === p.getID() && (!l.props.config.name || l.props.config.name === '')) {
                        outNameLinks++;
                    }
                }
            }
            if (ignoreSelf) {
                outFreeLinks--;
            }
            if (outFreeLinks > 1 && outNameLinks < 2) {
                if (!this.suppressBroadcast && userType !== 'client' && userType !== 'designer') {
                    window.apm.notification.post("error", window.l.notification.more_than_one_condition_free_transition);
                }
                this.setError("free::" + this.getID(), window.l.notification.more_than_one_condition_free_transition);
            } else if(outFreeLinks > 1 && outNameLinks > 1) {
                if (!this.suppressBroadcast && userType !== 'client' && userType !== 'designer') {
                    window.apm.notification.post("error", "Cannot have more than one transition without condition or name");
                }
                this.setError("free::" + this.getID(), "Cannot have more than one transition without condition or name");
            } else if(outFreeLinks < 2 && outNameLinks > 1) {
                if (!this.suppressBroadcast && userType !== 'client' && userType !== 'designer') {
                    window.apm.notification.post("error", "Cannot have more than one nameless transition");
                }
                this.setError("free::" + this.getID(), "Cannot have more than one nameless transition");
            } else {
                this.removeError("free::" + this.getID());
            }

            if (!this.suppressBroadcast) {
                document.dispatchEvent(new CustomEvent('ev-link-error', { detail: { self: this.getID(), source: sourcePort.getParent().getID() } }));
            }
        }
        this.setPreviousPort("source", null);
    }
    /**
     * Set error with key
     * @param {string} key Tracking key; error ID
     * @param {string} message
     */
    setError(key, message) {
        this.props.error[key] = message;
    }

    /**
     * Remove error with key
     * @param {string} key Tracking key; error ID
     */
    removeError(key) {
        delete this.props.error[key];
    }

    /**
     * Does this model have any error?
     * @returns {boolean}
     */
    hasError() {
        return Object.keys(this.props.error).length > 0;
    }

    /**
     * Get the last error message
     * @returns {string}
     */
    getError() {
        const e = Object.values(this.props.error);
        if (e.length) {
            return e[e.length - 1];
        }
        return "";
    }

    /**
     * Does this model have any conditions?
     * @returns {boolean}
     */
    hasCondition() {
        if (!this.getConfig("condition")) {
            return false;
        }
        const cond = this.getConfig("condition");
        if (cond.length <= 0) {
            return false;
        }
        var hasCond = false;
        for (var i = 0; i < cond.length; i++) {
            for (var j = 0; j < cond[i].length; j++) {
                if (cond[i][j]) {
                    hasCond = true;
                    break;
                }
            }
        }
        return hasCond;
    }

    /**
     * @param {BaseLinkModel} link
     */
    getEnableDisabledInFlowSwitch(link){
        const sourceNode = link.getSourceNode();
        const sourceNodeOutLinks = sourceNode?.getOutLinks() ?? [];
        const sourceNodeDisabledOutLinks = sourceNodeOutLinks.filter(l => l.props.config.disabledInFlow);
        const isDefaultLink = link.props.config.condition.length === 0;
        const isInitialDisabledLink = !sourceNode?.props.config.disabledInFlow && link.props.config.disabledInFlow; 
        const sourceHasAvailableOutLinks = sourceNodeOutLinks.length - sourceNodeDisabledOutLinks.length > 1;
        const sourceNodeLocaIntents = sourceNode?.props.config.localIntents ?? [];
        // remove localIntents that are not found in the components.intents array
        const availableSourceNodeLocaIntents = sourceNodeLocaIntents.filter(li => window.apm.serialized.components.intents.findIndex(i => i.id === li) !== -1);
        const sourceNodeLocaIntentsByName = availableSourceNodeLocaIntents.map(i => window.apm.serialized.components.intents.find(int => int.id === i).name)
        const linkReferencesSourceNodeProps = (link.props.config.serializedCondition) ? sourceNodeLocaIntentsByName.findIndex(li => {
            return link.props.config.serializedCondition.match(li)
            
        }) !== -1 : false;
        // always allow initial disabled link to be enabled
        if(isInitialDisabledLink) return true;
        
        if(isDefaultLink || linkReferencesSourceNodeProps) return false;
        
        if(sourceHasAvailableOutLinks) return true;

        return false;
    }

    /**
     * Get view configurations for property panel
     *
     * @returns {import("../types").ConfigView[]}
     */
    getConfigViews() {
        const viewConfig =  [
            ...[
                { type: "Input", 
                    label: this.getPropLabel("name"), 
                    key: "name" },
                { type: "Textarea", 
                    label: this.getPropLabel("description"),  
                    key: "description" },
                { type: "ScopeHint", 
                    userType: ['designer','developer','quality'], 
                    link: true },
                { type: "Condition",
                    userType: ['designer','developer','quality'],  
                    key: "condition" }
            ],
            ...this.props.configView
        ];

        if(!window.apm.serialized.displayPrefs['hideDisabledElements']){
            viewConfig.push({ type: "Switch", 
                                key: "disabledInFlow", 
                                label: "Disable this path", 
                                toggledLabel:"Enable this path",
                                userType: ['designer','developer','quality'],  
                                disabled: !this.getEnableDisabledInFlowSwitch(this) 
                            });
        }
                
        if(['designer','developer','quality'].indexOf(UserTypeStorage.getUserType()) !== -1){
            viewConfig.push({type: "hr"});
        }

        viewConfig.push({ type: "ItemList", 
                            label: this.getPropLabel("variables_referenced"), 
                            key: "inputs", 
                            view: "variableReference", 
                            userType: ['designer','developer','quality'], 
                            readOnly: true 
                        });

        return viewConfig;
    }


    getPropLabel(key) {
        return window.l.property[key];
    }

    updatePropsLastLocalChange({ id }) {
        Object.entries(
            window.apm
                .getCurrentDiagramWidget()
                .getGraphModel()
                .getModel().links
        ).forEach(([linkId, link]) => {
            if (linkId === id) {
                updateLastChange(link.props);
            }
        });
    }

    /**
     * Set originating port
     * @param {Object} port Port model
     */
    setSourcePort(port) {
        if (port !== null) {
            this.checkSourceOutDegree();
            super.setSourcePort(port);
        } else {
            if (this.sourcePort) {
                this.sourcePort.removeLink(this);
            }
            this.sourcePort = port;
            this.iterateListeners((listener) => {
                listener.sourcePortChanged && listener.sourcePortChanged(this, port);
            });
        }

        if (!this.suppressBroadcast && port) {
            // link has been modifed update timestamp
            this.updatePropsLastLocalChange(this);

        }

    }

    /**
     * Set destination port
     * @param {Object} port Port model
     */
    setTargetPort(port) {
        if (port !== null) {
            this.checkSourceOutDegree();
            super.setTargetPort(port);
        } else {
            if (this.targetPort) {
                this.targetPort.removeLink(this);
            }
            this.targetPort = port;
            this.iterateListeners((listener) => {
                listener.targetPortChanged && listener.targetPortChanged(this, port);
            });
        }

        if (!this.suppressBroadcast && port) {
            // link has been modifed update timestamp
            this.updatePropsLastLocalChange(this);

        }
    }

    /**
     * Link does not have a write proxy
     * @returns {boolean}
     */
    hasWriteProxy() {
        return false;
    }

    /**
     * Delete self
     */
    remove() {
        this.removeVariableRefs();
        window.apm.postEventToCurrentView({ target: "request-element-error-message", context: this });
        super.remove();
        this.checkSourceOutDegree(true);
    }

    /**
     * Serialize conditions
     */
    serializeCondition(condition) {
        var sc = [];
        for (var i = 0; i < condition.length; i++) {
            var sa = [];
            for (var j = 0; j < condition[i].length; j++) {
                sa.push("(" + condition[i][j] + ")");
            }
            sc.push("(" + sa.join(" && ") + ")");
        }
        if (sc.length) {
            return sc.join(" || ");
        }
        return "true";
    }

    isPureStatement(statement) {
        return !statement.match(/[&|]/i);
    }

    /**
     * Get own serialized condition in pretty print
     * @returns {*}
     */
    getPrettyCondition() {
        const condition = this.getConfig("condition");
        var sc = [];
        for (var i = 0; i < condition.length; i++) {
            var sa = [];
            for (var j = 0; j < condition[i].length; j++) {
                if (this.isPureStatement(condition[i][j])) {
                    sa.push(condition[i][j]);
                } else {
                    sa.push("(" + condition[i][j] + ")");
                }
            }
            const ssa = sa.join(" && ");
            if (this.isPureStatement(ssa)) {
                sc.push(ssa);
            } else {
                sc.push("(" + sa.join(" && ") + ")");
            }
        }
        if (sc.length) {
            return sc.join(" || ");
        }
        return "true";
    }

    /**
     * Serialize model for storage
     */
    serialize() {
        var s = super.serialize();
        s.source = this.sourcePort ? this.sourcePort.getParent().id : null;
        s.sourcePort = this.sourcePort ? this.sourcePort.name : null;
        s.target = this.targetPort ? this.targetPort.getParent().id : null;
        s.targetPort = this.targetPort ? this.targetPort.name : null;
        s.points = map(this.points, point => {
            return point.serialize();
        });
        delete s.type;
        delete s.extras;
        delete s.selected;
        s.props = JSON.parse(JSON.stringify(this.props));
        delete s.props.configView;

        return s;
    }
}
